"use client";

import { useMemo } from "react";
import Link from "next/link";
import type { Dictionary } from "@/i18n/dictionaries";
import type { Locale } from "@/i18n/config";
import type { BranchOption } from "@/lib/data/booking";
import { branchName as getBranchName, studioName as getStudioName } from "@/lib/data/types";
import { useBookingSelection } from "@/lib/booking-selection";

interface Props {
  locale: Locale;
  dict: Dictionary;
  productId: string;
  branches: BranchOption[];
  defaultStudioId?: string;
}

export function ExperienceBookLink({
  locale,
  dict,
  productId,
  branches,
  defaultStudioId,
}: Props) {
  const sel = useBookingSelection();
  const studioId = sel?.studioId ?? defaultStudioId;

  const resolved = useMemo(() => {
    if (!sel?.branchId) return null;
    const branch = branches.find((b) => b.id === sel.branchId);
    if (!branch) return null;
    const studio = branch.studios.find((s) => s.id === sel.studioId);
    return {
      branchName: getBranchName(branch, locale),
      studioName: studio ? getStudioName(studio, locale) : dict.booking.anyStudio,
    };
  }, [sel, branches, locale, dict.booking.anyStudio]);

  const href = `/${locale}/booking/${productId}${
    studioId ? `?studio=${encodeURIComponent(studioId)}` : ""
  }`;

  return (
    <div>
      {resolved && (
        <p className="mb-3 rounded-md bg-canvas px-4 py-2.5 text-xs leading-relaxed text-muted">
          <span className="text-faint">
            {dict.booking.selectedBranchLabel}
          </span>{" "}
          {resolved.branchName}
          {sel?.studioId ? ` · ${resolved.studioName}` : ""}
        </p>
      )}
      <Link href={href} className="btn-dark mt-8 w-full">
        {dict.detail.bookCta}
      </Link>
    </div>
  );
}
